
/*
 *  EventTrackingReportDialog - import as ES6 module, then use controller in modal dialog call.
 *  Ensure the following dependencies are in the consuming modules angular module declaration:
 *  "ngSanitize",
 *  "ngAnimate",
 *  "ui.bootstrap",
 *  "kendo.directives",
 *  EncodedHtmlDecoderServiceModule (import from services/EncodedHtmlDecoderService)
 *
 */

import angular from "angular";

import Assert from "tbAssert";

import ngSanitize from "angular-sanitize";
import ngAria from "angular-aria";
import ngAnimate from "angular-animate";
import uiBootstrap from "angular-ui-bootstrap";
import kendoDirectives from "../../shims/kendoDirectives";
import EncodedHtmlDecoderServiceModule from "../../services/EncodedHtmlDecoderService";

const mScopeSvc = new WeakMap();
const mSanitizeSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mModalInstance = new WeakMap();
const mEncodedHtmlDecoderSvc = new WeakMap();

const mTrackingReportData = new WeakMap();

const mTrackingReport = new WeakMap();
const mTrackingReportContent = new WeakMap();
const mTrackingReportContentIsReady = new WeakMap();
const mReportViewerIsReady = new WeakMap();

const mReportViewerOptions = new WeakMap();

class EventTrackingReportDialogController {
    constructor($scope, $sanitize, $log, $uibModalInstance, EncodedHtmlDecoder, trackingEventName, trackingReportData) {

        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($sanitize != null, "Assertion Failure: Missing Sanitize Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");

        Assert.StrictAssert($uibModalInstance != null, "Assertion Failure: Missing Modal Instance Dependency");
        Assert.StrictAssert(EncodedHtmlDecoder != null, "Assertion Failure: Missing Html Decoder Service Dependency");
        Assert.StrictAssert(trackingEventName != null, "Assertion Failure: Missing Tracking Event Name Dependency");
        Assert.StrictAssert(trackingReportData != null, "Assertion Failure: Missing Tracking Report Data Dependency");

        mScopeSvc.set(this, $scope);
        mSanitizeSvc.set(this, $sanitize);
        mLogSvc.set(this, $log);
        mModalInstance.set(this, $uibModalInstance);
        mEncodedHtmlDecoderSvc.set(this, EncodedHtmlDecoder);
        mTrackingReportData.set(this, trackingReportData);

        mTrackingReport.set(this, null);
        mTrackingReportContent.set(this, null);
        mTrackingReportContentIsReady.set(this, false);
        mReportViewerIsReady.set(this, false);

        mReportViewerOptions.set(this, {
            pdf: {
                author: "American Bridge Tracking",
                creator: "TrackerBase",
                margin: {
                    top: "0.5in",
                    bottom: "0.5in",
                    left: "0.5in",
                    right: "0.5in"
                },
                paperSize: [
                    "8.5in",
                    "11in"
                ],
                title: "TrackerBase Tracking Report: " + trackingEventName,
                fileName: "TrackerBase Tracking Report.pdf"
            },
            tools: []
        });

        $scope.$watch(
            () => mTrackingReportContent.get(this) ,
            () => {
                let trackingReportContent = mTrackingReportContent.get(this);
                mTrackingReportContentIsReady.set(this,
                    ((trackingReportContent != null) && (trackingReportContent.length > 0)));
            }
        );

        $scope.$on("kendoWidgetCreated", (e, widget) => {

            angular.noop(e);

            if (widget === $scope.trackingReportViewer) {
                mLogSvc.get(this).debug("Tracking Report Viewer Created.");

                let reportViewerElement = angular.element($scope.trackingReportViewer.body);
                reportViewerElement.removeAttr("contenteditable");

                mReportViewerIsReady.set(this, true);
            }
        });
    }

    get reportViewerOptions() {
        return mReportViewerOptions.get(this);
    }

    get trackingReportContentIsReady() {
        return mTrackingReportContentIsReady.get(this);
    }

    get trackingReportContent() {
        return mTrackingReportContent.get(this);
    }

    get reportViewerIsReady() {
        return mReportViewerIsReady.get(this);
    }

    $onInit() {
        if (mTrackingReportData.get(this) != null) {
            mTrackingReport.set(this, mTrackingReportData.get(this));
            mLogSvc.get(this).debug("Successfully loaded tracking report.");
        }
        else {
            mLogSvc.get(this).error("Error loading Tracking Report.");
            return;
        }

        let trackingReport = mTrackingReport.get(this);
        trackingReport.decodedReport =
            mEncodedHtmlDecoderSvc.get(this).decode(trackingReport.report);

        let trackingReportContent = mSanitizeSvc.get(this)(angular.copy(trackingReport.decodedReport));
        mTrackingReportContent.set(this, trackingReportContent);
    }

    exportToPDF() {
        if (mReportViewerIsReady.get(this) === true) {
            let reportExportCompleted = mScopeSvc.get(this).trackingReportViewer.saveAsPDF();

            reportExportCompleted.then(
                () => {
                    mLogSvc.get(this).info("Report Export Completed.");
                },
                () => {
                    mLogSvc.get(this).error("Report Export Failed.");
                }
            );
        }
    }

    ok() {
        mModalInstance.get(this).close();
    }

    cancel() {
        mModalInstance.get(this).dismiss("cancel");
    }
}

EventTrackingReportDialogController.$inject = [
    "$scope",
    "$sanitize",
    "$log",
    "$uibModalInstance",
    "EncodedHtmlDecoder",
    "trackingEventName",
    "trackingReportData"
];

EventTrackingReportDialogController.dependencies = [
    ngSanitize,
    ngAria,
    ngAnimate,
    uiBootstrap,
    kendoDirectives,
    EncodedHtmlDecoderServiceModule
];

EventTrackingReportDialogController.template = require("./EventTrackingReportDialog.tpl.html");

EventTrackingReportDialogController.controllerAs = "eventTrackingReportDialog";

export default EventTrackingReportDialogController;



//////////////////
// WEBPACK FOOTER
// ./src/views/event-tracking-report-dialog/EventTrackingReportDialog.js
// module id = wdMB
// module chunks = 1